import { RotateCcw, Unplug } from 'lucide-react';
import { useSeoMeta } from '@unhead/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { WotProgress } from '@/components/WotProgress';
import { WotReportView } from '@/components/WotReportView';
import { useWotScore } from '@/hooks/useWotScore';

/**
 * Runs the WoT analysis for a pubkey and renders progress, error, or the
 * finished report. Used by both the home page and NIP-19 report pages.
 */
export function WotReportSection({ pubkey }: { pubkey: string }) {
  const { data, isPending, isError, phase, refetch } = useWotScore(pubkey);

  const name =
    data?.metadata?.display_name ||
    data?.metadata?.name ||
    `${pubkey.slice(0, 12)}…`;

  useSeoMeta({
    title: data
      ? `${name} — Web of Trust ${data.total}/100 · TrustWeb`
      : 'TrustWeb — Nostr Web of Trust Score',
    description: data
      ? `${name} scores ${data.total}/100 (${data.grade.label}) on the Nostr web of trust: ${data.metrics.followers?.toLocaleString() ?? 'unknown'} followers, ${Math.round(data.metrics.mutualRatio * 100)}% follow-back ratio.`
      : 'Measure the web of trust score of any Nostr identity.',
  });

  if (isPending) {
    return (
      <div className="py-10">
        <WotProgress phase={phase} />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="mx-auto max-w-md border-destructive/40 bg-card/80">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <Unplug className="size-8 text-destructive" />
          <div>
            <p className="font-medium">Could not reach the relays</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The analysis timed out or relays refused the connection. Try again
              in a moment.
            </p>
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            <RotateCcw className="size-4" />
            Retry analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return <WotReportView result={data} />;
}
